import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Save, Rss, Layout, FileText, CheckSquare, List, MessageSquare, AlertCircle, Calendar } from "lucide-react";

export default function IDGForumSettingsTab() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/idg/settings/extended").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    try {
      await api.patch("/idg/settings/extended", data);
      window.dispatchEvent(new Event("idg:settings-updated"));
      toast.success("Setări forum salvate cu succes");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!data) return <div className="text-zinc-500 animate-pulse p-5">Se încarcă setările...</div>;

  const handleChange = (key, val) => setData((p) => ({ ...p, [key]: val }));
  
  const toggleArray = (key, val) => {
    const arr = data[key] || [];
    if (arr.includes(val)) handleChange(key, arr.filter(x => x !== val));
    else handleChange(key, [...arr, val]);
  };

  return (
    <div className="space-y-6" data-testid="idg-forum-settings-tab">
      
      {/* Forum Settings */}
      <section className="tf-surface tf-clip-card p-5 space-y-4">
        <h3 className="tf-section-title flex items-center gap-2"><Layout className="w-4 h-4 text-[var(--tf-primary)]" /> Forum Settings</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Toggle 
            label="Enable RSS feeds?" 
            checked={data.forums_rss} 
            onChange={(v) => handleChange("forums_rss", v)} 
          />
          <Toggle 
            label="Honor Pinned Topics (Fluid View)" 
            desc="Show pinned topics at the top when fluid mode is used."
            checked={data.forums_fluid_pinned} 
            onChange={(v) => handleChange("forums_fluid_pinned", v)} 
          />
        </div>

        <div className="space-y-2 mt-4">
          <label className="text-xs font-bold text-zinc-300 block">Default forum view</label>
          <div className="flex gap-4 flex-wrap">
            <Radio label="Table" desc="A traditional forum index table view" checked={data.forums_default_view === "table"} onChange={() => handleChange("forums_default_view", "table")} />
            <Radio label="Grid" desc="Display forums as cards in a grid" checked={data.forums_default_view === "grid"} onChange={() => handleChange("forums_default_view", "grid")} />
            <Radio label="Fluid" desc="A dynamic listing topics with a filter box" checked={data.forums_default_view === "fluid"} onChange={() => handleChange("forums_default_view", "fluid")} />
          </div>
        </div>

        <div className="space-y-2 mt-4">
          <label className="text-xs font-bold text-zinc-300 block">Members can choose?</label>
          <p className="text-[10px] text-zinc-500 mb-2">Select the views you will allow members to choose from.</p>
          <div className="flex gap-4 flex-wrap">
            <Checkbox label="Table" checked={data.forums_default_view_choose?.includes("table")} onChange={() => toggleArray("forums_default_view_choose", "table")} />
            <Checkbox label="Grid" checked={data.forums_default_view_choose?.includes("grid")} onChange={() => toggleArray("forums_default_view_choose", "grid")} />
            <Checkbox label="Fluid" checked={data.forums_default_view_choose?.includes("fluid")} onChange={() => toggleArray("forums_default_view_choose", "fluid")} />
          </div>
        </div>

      </section>

      {/* Topic Settings */}
      <section className="tf-surface tf-clip-card p-5 space-y-4">
        <h3 className="tf-section-title flex items-center gap-2"><FileText className="w-4 h-4 text-[var(--tf-primary)]" /> Topic Settings</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-xs font-bold text-zinc-300 block mb-1">Topics per page</label>
            <input type="number" min={5} max={100} className="tf-input max-w-xs" value={data.forums_topics_per_page || 25} onChange={e => handleChange("forums_topics_per_page", parseInt(e.target.value) || 25)} />
          </div>
          <div>
            <label className="text-xs font-bold text-zinc-300 block mb-1">Posts per page</label>
            <input type="number" min={2} max={250} className="tf-input max-w-xs" value={data.forums_posts_per_page || 25} onChange={e => handleChange("forums_posts_per_page", parseInt(e.target.value) || 25)} />
          </div>
        </div>

        <div className="space-y-2 mt-4">
          <label className="text-xs font-bold text-zinc-300 block">Default topic list view</label>
          <div className="flex gap-4 flex-wrap">
            <Radio label="Condensed View" desc="Show topics as a list" checked={data.forums_view_list_method === "list"} onChange={() => handleChange("forums_view_list_method", "list")} />
            <Radio label="Expanded View" desc="Show topics with a preview of the post" checked={data.forums_view_list_method === "snippet"} onChange={() => handleChange("forums_view_list_method", "snippet")} />
          </div>
        </div>

        <Toggle 
          label="Members can choose (Topic list view)?" 
          desc="Allow members to toggle between views. If no, members use the default view."
          checked={data.forums_view_list_choose} 
          onChange={(v) => handleChange("forums_view_list_choose", v)} 
        />

        <div className="mt-4 p-4 border border-white/5 bg-white/5 tf-clip-card-sm space-y-3">
          <label className="text-xs font-bold text-zinc-300 block">Popular Now</label>
          <p className="text-[10px] text-zinc-500 mb-2">Topics which are Popular Now are highlighted in the list.</p>
          <div className="flex items-center gap-2 text-xs">
            <input type="number" className="tf-input w-20 p-1 text-center" value={data.forums_popular_now_posts || 10} onChange={e => handleChange("forums_popular_now_posts", parseInt(e.target.value) || 10)} />
            <span className="text-zinc-400">posts in the last</span>
            <input type="number" className="tf-input w-24 p-1 text-center" value={data.forums_popular_now_minutes || 60} onChange={e => handleChange("forums_popular_now_minutes", parseInt(e.target.value) || 60)} />
            <span className="text-zinc-400">minutes</span>
          </div>
        </div>

      </section>

      {/* Question Settings */}
      <section className="tf-surface tf-clip-card p-5 space-y-4">
        <h3 className="tf-section-title flex items-center gap-2"><MessageSquare className="w-4 h-4 text-[var(--tf-primary)]" /> Question Settings</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Toggle label="Questions can be negatively rated?" checked={data.forums_questions_downvote} onChange={(v) => handleChange("forums_questions_downvote", v)} />
          <Toggle label="Answers can be negatively rated?" checked={data.forums_answers_downvote} onChange={(v) => handleChange("forums_answers_downvote", v)} />
        </div>

        <div className="space-y-2 mt-4">
          <label className="text-xs font-bold text-zinc-300 block">New Questions Criteria</label>
          <div className="flex flex-col gap-2">
            <Radio label="Best answer not chosen" desc="Questions where a best answer has not been chosen." checked={data.forums_new_questions === "0"} onChange={() => handleChange("forums_new_questions", "0")} />
            <Radio label="No answers" desc="Questions which have not received any answers." checked={data.forums_new_questions === "1"} onChange={() => handleChange("forums_new_questions", "1")} />
          </div>
        </div>

      </section>

      {/* Meta & Summary */}
      <section className="tf-surface tf-clip-card p-5 space-y-4">
        <h3 className="tf-section-title flex items-center gap-2"><AlertCircle className="w-4 h-4 text-[var(--tf-primary)]" /> Meta & Summary Settings</h3>
        
        <div className="space-y-2">
          <label className="text-xs font-bold text-zinc-300 block">Show in topics</label>
          <div className="flex gap-4">
            <Checkbox label="Large time gaps between posts" checked={data.forums_topics_show_meta_time} onChange={(v) => handleChange("forums_topics_show_meta_time", v)} />
            <Checkbox label="Moderation events between posts" checked={data.forums_topics_show_meta_moderation} onChange={(v) => handleChange("forums_topics_show_meta_moderation", v)} />
          </div>
        </div>

        <Toggle 
          label="Show moderator name alongside moderation events" 
          desc="If disabled, the moderator name is hidden from non-staff."
          checked={data.forums_mod_actions_anon} 
          onChange={(v) => handleChange("forums_mod_actions_anon", v)} 
        />

        <div className="mt-4">
          <label className="text-xs font-bold text-zinc-300 block mb-2">Topic Summary Settings (Devices & Position)</label>
          <div className="flex gap-4 mb-4">
            <Checkbox label="Desktops/tablets" checked={data.forums_topic_activity_desktop} onChange={(v) => handleChange("forums_topic_activity_desktop", v)} />
            <Checkbox label="Mobile devices" checked={data.forums_topic_activity_mobile} onChange={(v) => handleChange("forums_topic_activity_mobile", v)} />
          </div>
          <div className="flex gap-4 border-l-2 border-white/10 pl-4">
            <Radio label="Sidebar" checked={data.forums_topic_activity_desktop_pos === "sidebar"} onChange={() => handleChange("forums_topic_activity_desktop_pos", "sidebar")} />
            <Radio label="Under the first post" checked={data.forums_topic_activity_desktop_pos === "post"} onChange={() => handleChange("forums_topic_activity_desktop_pos", "post")} />
          </div>
        </div>

      </section>

      {/* Save */}
      <div>
        <button onClick={save} className="tf-btn-primary tf-clip-btn flex items-center gap-2">
          <Save className="w-4 h-4" /> Save Settings
        </button>
      </div>

    </div>
  );
}

// UI Components
function Toggle({ label, desc, checked, onChange }) {
  return (
    <div className="flex items-start gap-3">
      <button 
        type="button" 
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none ${checked ? 'bg-[var(--tf-primary)]' : 'bg-zinc-700'}`}
      >
        <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition duration-200 ease-in-out ${checked ? 'translate-x-2' : '-translate-x-2'}`} />
      </button>
      <div>
        <label className="text-xs font-bold text-zinc-200 cursor-pointer" onClick={() => onChange(!checked)}>{label}</label>
        {desc && <p className="text-[10px] text-zinc-500 mt-0.5">{desc}</p>}
      </div>
    </div>
  );
}

function Radio({ label, desc, checked, onChange }) {
  return (
    <label className="flex items-start gap-2 cursor-pointer p-2 hover:bg-white/5 rounded transition-colors">
      <input type="radio" className="mt-0.5 accent-[var(--tf-primary)]" checked={checked} onChange={onChange} />
      <div>
        <div className={`text-xs font-bold ${checked ? "text-[var(--tf-primary)]" : "text-zinc-300"}`}>{label}</div>
        {desc && <div className="text-[10px] text-zinc-500">{desc}</div>}
      </div>
    </label>
  );
}

function Checkbox({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer p-2 hover:bg-white/5 rounded transition-colors">
      <input type="checkbox" className="w-3.5 h-3.5 rounded accent-[var(--tf-primary)] bg-zinc-800 border-zinc-600" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="text-xs text-zinc-200">{label}</span>
    </label>
  );
}
